import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    HiOutlineExclamationTriangle,
    HiOutlineChartBar,
    HiOutlineCheckCircle,
    HiOutlineClock,
} from 'react-icons/hi2';
import { authService, plantService } from '../services';
import hazardReportService from '../services/hazardReportService';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import PageLoading from "../components/common/PageLoading";
import './HazardReportDashboard.css';

const HazardReportDashboard = () => {
    const navigate = useNavigate();
    const tenantId = authService.getTenantId();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [trends, setTrends] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [plants, setPlants] = useState([]);
    const [selectedPlantId, setSelectedPlantId] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 60 days
        endDate: new Date().toISOString().split('T')[0],
    });

    const handlePlantChange = (val) => {
        setSelectedPlantId(val);
        if (val) {
            localStorage.setItem('defaultPlantId', val);
        }
    };

    useEffect(() => {
        const loadPlants = async () => {
            try {
                const plantsData = await plantService.getAllPlants();
                setPlants(plantsData || []);
                // Cache plants
                sessionStorage.setItem('plants_data', JSON.stringify(plantsData || []));

                // Auto-select if exactly one plant exists
                if (plantsData?.length === 1) {
                    setSelectedPlantId(plantsData[0].plantId.toString());
                }
            } catch (err) {
                console.error('Error loading plants:', err);
            }
        };
        loadPlants();
    }, []);

    useEffect(() => {
        const loadDashboardData = async () => {
            setLoading(true);
            setError('');
            try {
                const filterParams = {
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    plantId: selectedPlantId || undefined
                };

                const [trendsData, statsData] = await Promise.all([
                    hazardReportService.getTrends(filterParams),
                    hazardReportService.getStatistics(filterParams)
                ]);

                setTrends(trendsData);
                setStatistics(statsData);
            } catch (err) {
                setError('Failed to load dashboard data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (tenantId) {
            loadDashboardData();
        }
    }, [tenantId, selectedPlantId, dateRange]);

    // Enhanced Chart Components (Local)
    const PieChart = ({ data, size = 200 }) => {
        const total = data.reduce((sum, item) => sum + item.value, 0);

        const handleSliceClick = (itemName) => {
            let filterPath = '/home/hazard-reports';
            if (itemName === 'Pending') filterPath += '?approvalStatus=Pending';
            else if (itemName === 'Approved') filterPath += '?approvalStatus=Approved';
            else if (itemName === 'Rejected') filterPath += '?approvalStatus=Rejected';
            else if (itemName === 'Open') filterPath += '?status=Open';
            navigate(filterPath);
        };

        if (total === 0) return <div className="chart-empty">No data available</div>;

        let currentAngle = -90;
        const radius = size / 2 - 10;
        const center = size / 2;

        const segments = data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            if (percentage === 0) return null;

            const angle = (percentage / 100) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + angle;
            currentAngle = endAngle;

            const x1 = center + radius * Math.cos((startAngle * Math.PI) / 180);
            const y1 = center + radius * Math.sin((startAngle * Math.PI) / 180);
            const x2 = center + radius * Math.cos((endAngle * Math.PI) / 180);
            const y2 = center + radius * Math.sin((endAngle * Math.PI) / 180);
            const largeArc = angle > 180 ? 1 : 0;

            return (
                <path
                    key={index}
                    d={`M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={item.color}
                    stroke="var(--bg-card)"
                    strokeWidth="2"
                    className="pie-slice"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSliceClick(item.name)}
                />
            );
        }).filter(Boolean);

        return (
            <div className="pie-chart-container">
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{segments}</svg>
                <div className="pie-legend">
                    {data.map((item, i) => (
                        <div key={i} className="legend-item">
                            <span className="legend-color" style={{ backgroundColor: item.color }}></span>
                            <span>{item.name}: <strong>{item.value}</strong></span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const BarChart = ({ data, height = 250 }) => {
        if (!data || data.length === 0) return <div className="chart-empty">No data available</div>;

        // Clean and sort data
        const chartData = [...data]
            .map(item => ({
                ...item,
                categoryName: item.categoryName || 'Other'
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8); // Top 8 for clarity

        const max = Math.max(...chartData.map(d => d.count), 1);

        return (
            <div className="bar-chart-container" style={{ height: `${height}px` }}>
                {chartData.map((item, i) => (
                    <div key={i} className="bar-group">
                        <div className="bar-tooltip">
                            {item.categoryName}: {item.count}
                        </div>
                        <div
                            className="bar"
                            style={{
                                height: `${(item.count / max) * 100}%`,
                                // Use a nice palette
                                background: i === 0
                                    ? 'linear-gradient(to top, #4f46e5, #818cf8)'
                                    : `linear-gradient(to top, hsl(${220 + (i * 25) % 80}, 70%, 50%), hsl(${220 + (i * 25) % 80}, 60%, 70%))`
                            }}
                            onClick={() => navigate(`/home/hazard-reports?category=${encodeURIComponent(item.categoryName)}`)}
                        ></div>
                        <div className="bar-label" title={item.categoryName}>
                            {item.categoryName}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const LineChart = ({ data, height = 250 }) => {
        if (!data || data.length === 0) return <div className="chart-empty">No data available</div>;
        const max = Math.max(...data.map(d => d.count), 1);
        const padding = 20;
        const width = 200; // Increased viewBox width for better resolution

        const points = data.map((d, i) => {
            const x = (i / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
            const y = 100 - ((d.count / max) * 80 + 10);
            return `${x},${y}`;
        }).join(' ');

        return (
            <div className="line-chart-container" style={{ height: `${height}px` }}>
                <svg viewBox={`0 0 ${width} 100`} preserveAspectRatio="none" className="line-chart-svg">
                    <polyline points={points} fill="none" stroke="var(--accent-primary)" strokeWidth="0.5" />
                    {data.map((d, i) => {
                        const x = (i / (data.length - 1 || 1)) * (width - 2 * padding) + padding;
                        const y = 100 - ((d.count / max) * 80 + 10);
                        return <circle key={i} cx={x} cy={y} r="1" fill="var(--accent-primary)" />;
                    })}
                </svg>
            </div>
        );
    };

    if (loading && !trends) return (
        <div className="hazard-dashboard">
            <PageLoading message="Loading Dashboard..." />
        </div>
    );

    return (
        <div className="pagecontainer hazard-dashboard">
            <div className="page-header">
                <div>
                    <h1><HiOutlineExclamationTriangle className="dashboard-icon" /> Hazard Reports Analytics</h1>
                    <p className="dashboard-subtitle">Monitor site hazards and safe workspace metrics</p>
                </div>
                <div className="header-actions">
                    <div className="filters-row">
                        <div className="date-range-selector">
                            <label className='fw-bold'>From:</label>
                            <input type="date" value={dateRange.startDate} onChange={e => setDateRange({ ...dateRange, startDate: e.target.value })} />
                            <label className='fw-bold'>To:</label>
                            <input type="date" value={dateRange.endDate} onChange={e => setDateRange({ ...dateRange, endDate: e.target.value })} />
                        </div>
                        <div className="plant-filter">
                            <label className='fw-bold'>Plant:</label>
                            <select value={selectedPlantId} onChange={e => handlePlantChange(e.target.value)}>
                                <option value="">All Plants</option>
                                {plants.map(p => <option key={p.plantId} value={p.plantId}>{p.plantName}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {trends && (
                <div className="metrics-grid-compact">
                    <div className="metric-card-compact total" onClick={() => navigate('/home/hazard-reports')} style={{ cursor: 'pointer' }}>
                        <HiOutlineChartBar className="metric-icon-compact" />
                        <div className="metric-content-compact">
                            <div className="metric-value-compact">{trends.totalHazards || 0}</div>
                            <div className="metric-label-compact">Total Hazards</div>
                        </div>
                    </div>
                    <div className="metric-card-compact danger" onClick={() => navigate('/home/hazard-reports?isImmediateDanger=true')} style={{ cursor: 'pointer' }}>
                        <HiOutlineExclamationTriangle className="metric-icon-compact" style={{ color: '#ef4444' }} />
                        <div className="metric-content-compact">
                            <div className="metric-value-compact">{trends.immediateDanger || 0}</div>
                            <div className="metric-label-compact">Immediate Danger</div>
                        </div>
                    </div>
                    <div className="metric-card-compact pending" onClick={() => navigate('/home/hazard-reports?approvalStatus=Pending')} style={{ cursor: 'pointer' }}>
                        <HiOutlineClock className="metric-icon-compact" style={{ color: '#f59e0b' }} />
                        <div className="metric-content-compact">
                            <div className="metric-value-compact">{trends.pendingApprovals || 0}</div>
                            <div className="metric-label-compact">Pending Approval</div>
                        </div>
                    </div>
                    <div className="metric-card-compact resolved" onClick={() => navigate('/home/hazard-reports?approvalStatus=Approved')} style={{ cursor: 'pointer' }}>
                        <HiOutlineCheckCircle className="metric-icon-compact" style={{ color: SEMANTIC_SUCCESS_MID }} />
                        <div className="metric-content-compact">
                            <div className="metric-value-compact">{trends.approvedHazards || 0}</div>
                            <div className="metric-label-compact">Approved</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Approval Status</h3>
                        <span className="chart-subtitle">Breakdown by approval workflow state</span>
                    </div>
                    <div className="chart-body">
                        <PieChart data={[
                            { name: 'Approved', value: trends?.approvedHazards || 0, color: SEMANTIC_SUCCESS_MID },
                            { name: 'Pending', value: trends?.pendingApprovals || 0, color: '#f59e0b' },
                            { name: 'Rejected', value: trends?.rejectedHazards || 0, color: '#ef4444' },
                            { name: 'Open', value: trends?.openHazards || 0, color: '#a855f7' }
                        ]} />
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Severity Levels</h3>
                        <span className="chart-subtitle">Distribution of reported hazard severity</span>
                    </div>
                    <div className="chart-body">
                        <PieChart data={(statistics?.severityDistribution || []).map((s, i) => ({
                            name: s.severityLevelName,
                            value: s.count,
                            color: s.severityLevelName.toLowerCase().includes('high') ? '#ef4444' :
                                   s.severityLevelName.toLowerCase().includes('med') ? '#f59e0b' :
                                   s.severityLevelName.toLowerCase().includes('low') ? '#3b82f6' :
                                   `hsl(${120 + i * 40}, 70%, 50%)`
                        }))} />
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Category Analysis</h3>
                        <span className="chart-subtitle">Top contributing hazard categories</span>
                    </div>
                    <div className="chart-body">
                        <BarChart data={statistics?.categoryDistribution || []} />
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Department Breakdown</h3>
                        <span className="chart-subtitle">Hazard reports by workspace department</span>
                    </div>
                    <div className="chart-body">
                        <BarChart data={(statistics?.departmentDistribution || []).map(d => ({
                            categoryName: d.departmentName,
                            count: d.count
                        }))} />
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Immediate Action Status</h3>
                        <span className="chart-subtitle">Proportion of hazards addressed immediately</span>
                    </div>
                    <div className="chart-body">
                        <PieChart data={[
                            { name: 'Action Taken', value: statistics?.immediateActionStats?.taken || 0, color: '#0ea5e9' },
                            { name: 'Pending Action', value: statistics?.immediateActionStats?.pending || 0, color: '#cbd5e1' }
                        ]} />
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3>Reporting Trend (Last 15 Days)</h3>
                        <span className="chart-subtitle">Daily hazard reports over time</span>
                    </div>
                    <div className="chart-body">
                        {(() => {
                            const points = [];
                            const today = new Date();
                            for (let i = 14; i >= 0; i--) {
                                const d = new Date(today);
                                d.setDate(today.getDate() - i);
                                const dateStr = d.toISOString().split('T')[0];
                                const existing = statistics?.dailyTrend?.find(t => t.date === dateStr);
                                points.push({ date: dateStr, count: existing ? existing.count : 0 });
                            }
                            return <LineChart data={points} />;
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HazardReportDashboard;
